<script lang="ts" setup>
import moment from 'moment';
import { useRouter } from 'vue-router';
import type { z } from 'zod';

import { BookingSchema } from '~/schemas/booking';

import CardLoading from '~/components/common/CardLoading.vue';
import PopoverDatePicker from '~/components/common/PopoverDatePicker.vue';

type State = { date: string; count: number } & Partial<z.infer<typeof BookingSchema>>;

const router = useRouter();
const bookingStore = useBookingStore();
const authStore = useAuthStore();

useSeoMeta({
  title: 'Booking'
});

const date = ref<Date>(new Date());
const availableTimes = ref<string[]>([]);

const state = reactive<State>({
  date: moment(date.value).format('DD/MM/YYYY'),
  count: 1,
  fullName:
    authStore.profile?.fullName !== authStore.profile?.user.email
      ? authStore.profile?.fullName
      : undefined,
  email: authStore.profile?.user.email
});

const formatedDate = computed<string>(() => {
  return moment(date.value).format('DD/MM/YYYY');
});

const isLoading = ref<boolean>(false);

const handleSubmit = async (): Promise<void> => {
  try {
    isLoading.value = true;

    await bookingStore.create({
      count: state.count,
      date: state.date,
      email: state.email!,
      fullName: state.fullName!,
      phoneNumber: state.phoneNumber!,
      time: state.time!
    });

    await router.push({ name: 'menu' });
  } catch {
    isLoading.value = false;
  }
};

const onSubmit = (): void => {
  handleSubmit();
};

const onUpdateDate = () => {
  state.date = formatedDate.value;
  loadingAvailableDates();
};

const onDecreasePeople = () => {
  state.count--;
};

const onIncreasePeople = () => {
  state.count++;
};

const loadingAvailableDates = async (): Promise<void> => {
  availableTimes.value = await bookingStore.getAvailableTimes(date.value);
  if (state.time && !availableTimes.value.includes(state.time)) {
    state.time = '';
  }
};

onMounted(async () => {
  await loadingAvailableDates();
});
</script>

<template>
  <UContainer class="booking min-h-svh flex flex-col items-center">
    <Transition
      name="component"
      mode="out-in"
    >
      <div
        v-if="isLoading"
        class="pt-12"
      >
        <CardLoading
          data-test="loading"
          title="Booking..."
        />
      </div>

      <UForm
        v-else
        class="py-4 sm:pt-20 sm:max-w-[40rem] w-full"
        :schema="BookingSchema"
        :state="state"
        @submit="onSubmit()"
      >
        <UCard
          class="booking__card shadow-2xl"
          :ui="{ body: { base: 'space-y-4' } }"
        >
          <template #header>
            <div class="flex flex-col text-center">
              <div class="flex gap-1 items-center font-semibold text-2xl justify-center">
                <UIcon name="ic:round-event text-3xl" />
                <h1>Booking</h1>
              </div>

              <h2 class="subtext-card">
                With friends, family, parties & events
              </h2>
            </div>
          </template>

          <div class="grid gap-x-4 gap-y-4 grid-cols-12">
            <UFormGroup
              label="Date"
              name="date"
              class="col-span-6 sm:col-span-4"
            >
              <PopoverDatePicker
                v-model="date"
                :min-date="new Date()"
                @update:model-value="onUpdateDate"
              >
                <div class="
                  w-full
                  rounded-md
                  ring-1
                  ring-inset
                  ring-card
                  bg-card-contrast
                  py-2.5
                  px-3.5
                  flex
                  gap-1.5
                  items-center
                  overflow-hidden
                ">
                  <span class="pr-1 flex items-center">
                    <UIcon
                      class="text-2xl subtext-card"
                      name="ic:round-event"
                    />
                  </span>

                  <span
                    class="w-full"
                    data-test="date"
                  >
                    {{ formatedDate }}
                  </span>
                </div>
              </PopoverDatePicker>
            </UFormGroup>

            <UFormGroup
              label="Time"
              name="time"
              class="col-span-6 sm:col-span-4"
            >
              <USelectMenu
                v-model="state.time"
                :options="availableTimes"
                aria-label="select time"
                icon="ic:round-access-time"
                class="bg-card-contrast
                  rounded-md
                  ring-1
                  ring-inset
                  ring-card
                "
                size="md"
              />
            </UFormGroup>

            <UFormGroup
              label="How many people"
              name="count"
              class="col-span-6 sm:col-span-4"
            >
              <div class="
                w-full
                rounded-md
                ring-1
                ring-inset
                ring-card
                bg-card-contrast
                py-1.5
                px-3.5
                flex
                gap-1.5
                justify-between
                items-center
                overflow-hidden
              ">
                <span class="pr-1 flex items-center">
                  <UIcon
                    class="text-2xl subtext-card"
                    name="ic:round-groups"
                  />
                </span>

                <UButton
                  class="rounded-full text-card"
                  icon="ic:round-minus"
                  variant="ghost"
                  aria-label="minus"
                  :disabled="state.count <= 1"
                  @click="onDecreasePeople()"
                />

                <span
                  class="flex-1 text-center"
                  data-test="date"
                >
                  {{ state.count }}
                </span>

                <UButton
                  class="rounded-full text-card"
                  icon="ic:round-add"
                  variant="ghost"
                  aria-label="add"
                  @click="onIncreasePeople()"
                />
              </div>
            </UFormGroup>

            <UFormGroup
              label="Phone"
              name="phoneNumber"
              class="col-span-6 sm:col-span-5"
            >
              <UInput
                v-model="state.phoneNumber"
                icon="ic:round-phone"
                size="xl"
              />
            </UFormGroup>

            <UFormGroup
              label="Full name"
              name="fullName"
              class="col-span-12 sm:col-span-7"
            >
              <UInput
                v-model="state.fullName"
                icon="ic:round-person"
                size="xl"
              />
            </UFormGroup>

            <UFormGroup
              label="Email"
              name="email"
              class="col-span-12"
            >
              <UInput
                v-model="state.email"
                icon="ic:round-person"
                size="xl"
              />
            </UFormGroup>
          </div>

          <template #footer>
            <UButton
              block
              class="bg-company-1 py-4 font-semibold uppercase"
              label="Booking now"
              type="submit"
              data-test="submit"
            />
          </template>
        </UCard>
      </UForm>
    </Transition>
  </UContainer>
</template>

<style lang="scss" scoped>
.component-enter-active,
.component-leave-active {
  transition: all 0.2s ease;

  .booking__card {
    div {
      transition: all 0.2s ease;
    }
  }
}

.component-enter-from,
.component-leave-to {
  transform: scale(0.85);

  .booking__card {
    div {
      opacity: 0;
    }
  }
}

.component-leave-from,
.component-enter-to {
  .booking__card {
    div {
      opacity: 1;
    }
  }
}

.booking {
  padding-top: var(--header-height);
}
</style>
